import React, {
  ChangeEvent,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useState,
} from 'react'

import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { IoAddOutline } from 'react-icons/io5'

import LogsSection from './LogsSection'
import {
  Button,
  DateCalendar,
  ErrorModal,
  Spinner,
  Textarea,
} from '../../../../..'
import { useStorage, useStudents, useUser } from '../../../../../../context'
import { useLogForm } from '../../../../../../hooks/useLogForm'
import { NewLog, PatchableKeys } from '../../../../../../types'
import { Spacer } from '../../../../../global'
import { InputFile } from '../../../../../inputs'
import ExerciseAccordion from '../ExerciseAccordion'
import { ItemContainer, LogsContainer } from '../styles'

interface LogProps {
  logData: NewLog
  setLogData: Dispatch<SetStateAction<NewLog | null>>
}

const Log = ({ logData, setLogData }: LogProps) => {
  const { setOnEditionMode, onEditionMode } = useUser()
  const { loadingStorage, setProgessBar } = useStorage()
  const {
    activeStudent,
    uploadLog,
    saveStudent,
    deleteLog,
    setOnLogCreation,
    onLogCreation,
  } = useStudents()

  const { state, patch, setDate, setAudio, addExercise, replace } =
    useLogForm(logData)

  const [saveMessage, setSaveMessage] = useState<string | null | ReactNode>(
    null,
  )
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const handleUpdate = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target
      patch(name as PatchableKeys, value)
    },
    [patch],
  )

  const resetStates = useCallback(
    (wasSaving: boolean) => {
      setOnEditionMode(false)
      setOnLogCreation(false)
      if (wasSaving) setSaving(false)
      else setProgessBar(0)
    },
    [setOnEditionMode, setLogData, setOnLogCreation, setProgessBar],
  )

  const handleSaveLog = useCallback(async () => {
    if (!activeStudent) return
    setSaving(true)

    const nextAttendance = (activeStudent?.currentAttendance || 0) + 1
    // Normalizar audio para asegurar que se guarde como arreglo

    const normalizedState = {
      ...state,
      audio: Array.isArray(state.audio)
        ? state.audio
        : state.audio
          ? [state.audio]
          : [],
    }

    const response = await uploadLog(
      activeStudent.uid,
      normalizedState,
      activeStudent.activeBonus,
    )

    if (response.error) {
      setSaveMessage(response.message)
      setShowModal(true)
    } else {
      const updatedStudent = {
        ...activeStudent,
        currentAttendance: nextAttendance,
      }
      await saveStudent(updatedStudent, activeStudent.uid)
      // setSaveMessage(response.message)
      toast.success(
        `Bitacora ${onLogCreation ? `creada` : `actualizada`} con exito`,
      )
    }

    resetStates(true)
  }, [activeStudent, state, uploadLog, saveStudent, resetStates])

  const handleClose = async () => {
    try {
      if (onEditionMode) {
        if (!activeStudent || !logData.uid || !logData.professorUID) return
        if (onLogCreation) {
          await deleteLog(activeStudent.uid, logData.uid, logData.professorUID)
        }
      }
    } catch (error) {
      console.error('Error deleting log on close:', error)
    } finally {
      resetStates(false)
      setLogData(null)
    }
  }

  console.log(logData, 'logData')

  return (
    <>
      {saveMessage && showModal && (
        <ErrorModal
          setOpenModal={setShowModal}
          errorMessage={saveMessage}
          setError={setSaveMessage}
        />
      )}
      {saving ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spinner />
        </div>
      ) : (
        <LogsContainer>
          <ItemContainer>
            {/* TODO ARREGLAR EN MOBILE */}
            <label htmlFor="dateCalendar">Fecha</label>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <DateCalendar
                referenceDate={state.date}
                handleDate={setDate}
                defaultValue={dayjs(state.date)}
                fullWidth={false}
                disabled={!onEditionMode}
              />
              <Button
                label={onEditionMode ? 'CANCELAR' : 'CERRAR'}
                onClick={handleClose}
                className="dark-grey"
                disabled={
                  (logData?.audio?.length ?? 0) !== (state.audio?.length ?? 0)
                }
                fontSize={14}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <label htmlFor="objetives">Objetivos prioritarios</label>
            <Textarea
              name="objetives"
              id="objetives"
              onChange={handleUpdate}
              placeholder="Agregar objetivo"
              readOnly={!onEditionMode}
              value={state.objetives ?? ''}
            />
          </ItemContainer>

          {!!state.exercises?.length && (
            <ExerciseAccordion
              data={state}
              setLogData={(fn) => {
                const next = typeof fn === 'function' ? (fn as any)(state) : fn
                if (next) replace(next)
              }}
            />
          )}
          {/* NO FUNCIONA! */}
          {onEditionMode && (
            <Button
              width="142px"
              className="dark-grey prof-btn"
              onClick={addExercise}
              label={
                <>
                  <IoAddOutline />
                  Ejercicio
                </>
              }
            />
          )}
          <LogsSection
            sectionNumber="2"
            sectionTitle="Repertorio"
            childrens={[
              {
                type: 'input',
                label: 'Título de la canción',
                placeholder: 'Agregar titulo',
                id: 'songTitle',
                defaultValue: state.songTitle ?? '',
              },
              {
                type: 'text',
                label: 'Observaciones de la canción',
                placeholder: 'Agregar observaciones',
                id: 'songObservations',
                defaultValue: state.songObservations ?? '',
              },
            ]}
            handleUpdate={handleUpdate}
          />
          <LogsSection
            sectionNumber="3"
            sectionTitle="Tareas/Acuerdos"
            childrens={[
              {
                type: 'text',
                label: 'Tareas/ Acuerdos para la siguiente clase',
                placeholder: 'Agregar una tarea',
                id: 'agreement',
                defaultValue: state.agreement ?? '',
              },
            ]}
            handleUpdate={handleUpdate}
          />
          <LogsSection
            sectionNumber="4"
            sectionTitle="Observaciones generales"
            childrens={[
              {
                type: 'text',
                label: 'Observaciones',
                placeholder: 'Agregar observaciones',
                id: 'generalObserbations',
                defaultValue: state.generalObserbations ?? '',
              },
            ]}
            handleUpdate={handleUpdate}
          />

          <LogsSection sectionNumber="5" sectionTitle="Agregar Audio">
            <ItemContainer>
              <label htmlFor="inputFile">
                Link al audio de la clase
                {loadingStorage && (
                  <span
                    style={{
                      background: '#BCDDEB',
                      padding: '4px 6px',
                      marginLeft: 10,
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  >
                    {saving ? 'Eliminando...' : 'Cargando...'}
                  </span>
                )}
              </label>

              <InputFile
                userId={activeStudent?.uid}
                logUID={logData.uid}
                readOnly={!onEditionMode}
                defaultValue={state.audio}
                updateLogData={(files) => {
                  // files ya es la lista FINAL (o undefined)
                  setAudio(files)
                }}
              />
            </ItemContainer>
          </LogsSection>
          <Spacer height={32} />
          <ItemContainer style={{ alignItems: 'center' }}>
            {onEditionMode ? (
              <Button
                onClick={handleSaveLog}
                className="dark-grey"
                disabled={saving || loadingStorage}
                label="GUARDAR"
                fontSize={14}
              />
            ) : (
              <Button
                onClick={() => setOnEditionMode(true)}
                className="dark-grey"
                fontSize={14}
                label="EDITAR"
              />
            )}
          </ItemContainer>
        </LogsContainer>
      )}
    </>
  )
}

export default Log

/* {logData.exercises?.length > 0 && (
            <ExerciseAccordion data={logData} setLogData={setLogData} />
          )} */
/* <InputFile
                userId={activeStudent?.uid}
                logUID={logData.uid}
                updateLogData={setAudio}
                readOnly={!onEditionMode}
                defaultValue={state.audio}
              /> */

// adapter: usar updates funcionales para evitar race conditions
// updateLogData={(payload) => {
//   console.log('payload', payload)
//   if (!payload) {
//     setAudio(undefined)
//     return
//   }

//   if (Array.isArray(payload)) {
//     // anexar lista de nuevos audios
//     setAudio((prev) => {
//       const existing = Array.isArray(prev)
//         ? prev
//         : prev
//           ? [prev]
//           : []
//       return [...existing, ...payload]
//     })
//     return
//   }

//   // payload es un solo archivo -> anexarlo con actualización funcional
//   setAudio((prev) => {
//     const existing = Array.isArray(prev)
//       ? prev
//       : prev
//         ? [prev]
//         : []
//     return [...existing, payload]
//   })
// }}
